import React from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// ============ FULL SCREEN OVERLAY LOADER ============
export function FullScreenLoader({ message = "Processing... Please wait", show = false }) {
  if (!show) return null;
  return (
    <div
      data-testid="fullscreen-loader"
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(15,23,42,0.35)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4 max-w-sm mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-600 animate-spin" />
          <div className="absolute inset-2 rounded-full border-[2px] border-transparent border-b-violet-500 animate-spin" style={{ animationDirection: "reverse", animationDuration: "0.8s" }} />
        </div>
        <p className="text-sm font-medium text-slate-700 text-center">{message}</p>
      </div>
    </div>
  );
}

// ============ STEPPED PROGRESS LOADER ============
export function SteppedLoader({ steps = [], currentStep = 0, show = false }) {
  if (!show) return null;
  return (
    <div
      data-testid="stepped-loader"
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(15,23,42,0.35)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-5 max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-600 animate-spin" />
        </div>
        <div className="w-full space-y-2.5">
          {steps.map((step, i) => (
            <div key={i} className={`flex items-center gap-3 text-sm transition-all duration-300 ${i < currentStep ? "text-emerald-600" : i === currentStep ? "text-indigo-700 font-medium" : "text-slate-400"}`}>
              {i < currentStep ? (
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
              ) : i === currentStep ? (
                <Loader2 className="w-4 h-4 animate-spin text-indigo-600 shrink-0" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
              )}
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ TABLE SKELETON LOADER ============
export function TableSkeleton({ columns = 6, rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr key={rowIdx} className="border-b border-slate-100" style={{ animationDelay: `${rowIdx * 60}ms` }}>
          <td className="px-4 py-3.5"><Skeleton className="w-4 h-4 rounded" /></td>
          <td className="px-4 py-3.5"><Skeleton className="w-5 h-4 rounded" /></td>
          {Array.from({ length: columns }).map((_, colIdx) => (
            <td key={colIdx} className="px-4 py-3.5">
              <Skeleton className={`h-4 rounded ${colIdx === 0 ? "w-32" : colIdx === 1 ? "w-24" : "w-20"}`} />
              {colIdx === 0 && <Skeleton className="h-3 w-20 rounded mt-1.5" />}
            </td>
          ))}
          <td className="px-4 py-3.5">
            <div className="flex gap-2 justify-center">
              <Skeleton className="w-7 h-7 rounded-lg" />
              <Skeleton className="w-7 h-7 rounded-lg" />
              <Skeleton className="w-7 h-7 rounded-lg" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

// ============ LIST SKELETON LOADER ============
export function ListSkeleton({ count = 5 }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5" style={{ animationDelay: `${i * 60}ms` }}>
          <div className="flex items-start gap-4">
            <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40 rounded" />
              <Skeleton className="h-3 w-28 rounded" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>
          <div className="flex gap-6 mt-3 pt-3 border-t border-slate-100">
            <Skeleton className="h-3 w-24 rounded" />
            <Skeleton className="h-3 w-32 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ GRID SKELETON LOADER ============
export function GridSkeleton({ count = 8 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5" style={{ animationDelay: `${i * 50}ms` }}>
          <div className="flex items-center justify-between mb-3">
            <Skeleton className="h-5 w-28 rounded" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-3 w-36 rounded mb-2" />
          <Skeleton className="h-3 w-24 rounded mb-4" />
          <div className="flex gap-2 pt-3 border-t border-slate-100">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </>
  );
}

// ============ QUICK VIEW SKELETON ============
export function QuickViewSkeleton() {
  return (
    <div className="space-y-3 animate-in fade-in duration-200">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i}>
          <Skeleton className="h-2.5 w-16 rounded mb-1" />
          <Skeleton className="h-4 w-48 rounded" />
        </div>
      ))}
    </div>
  );
}

// ============ SECTION LOADER ============
export function SectionLoader({ message = "Loading..." }) {
  return (
    <div data-testid="section-loader" className="flex flex-col items-center justify-center py-12 gap-3">
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
        <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-600 animate-spin" />
      </div>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}
